<script setup lang="ts">
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import { firstOf } from '~/utils/queryUtils'
import { setTitle } from '~/utils/dom'
import { forceColorShade } from '~/utils/colors'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useMedia } from '~/composables/useMedia'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import useCardStyles from '~/composables/useCardStyles'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import HeaderViewBannerGrid from '~/components/utils/HeaderViewBannerGrid.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import HeaderTeacherButton from '~/components/subject/HeaderTeacherButton.vue'
import HeaderStencil from '~/components/subject/HeaderStencil.vue'
import Header8To10Section from '~/components/subject/gradetypes/Header8To10Section.vue'
import HeaderViewSkeleton from '~/components/skeletons/HeaderViewSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import AssignTaskDialog from '~/components/products/exams/assignment/AssignTaskDialog.vue'
import Image from '~/components/media/Image.vue'

const { locationId } = defineProps<{ locationId: number }>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { getProductFromResource } = useProductStore()
const { resolveBaseUrl } = useUrlBuilder()
const { set: setAppColor, clear: clearAppColor } = useAppColor()
const { theme } = useColorTheme(ColorTheme.BlueOrange)
const { isTeacher } = storeToRefs(useAuthStore())
const { cardStyle, themeStyle } = useCardStyles(theme)
const { isContentHeader } = useContentHelper()

const { data, isLoading, isError, error } = useQuery({
  queryKey: computed(() => ['task-header', locationId]),
  queryFn: async () => {
    const [header, sections] = await Promise.all([
      findContents<ContentProductHeader | ContentProductPackage>({
        locationIdCriterion: [locationId],
        contentTypeCriterion: [ContentType.ProductHeader, ContentType.ProductPackage],
      }, 1).then(([data]) => data),
      findContents<ContentProductHeader | ContentProductPackage>({
        parentLocationIdCriterion: [locationId],
        contentTypeCriterion: [ContentType.ProductHeader, ContentType.ProductPackage],
        sortField: 'priority',
        sortOrder: 'asc',
      }),
    ])
    return { header, sections }
  },
  enabled: computed(() => !!locationId),
  staleTime: Infinity,
  retry: 1,
})

const selectionMap = ref<Record<number, number>>({})

const taskLocationIds = computed(() => Object.values(selectionMap.value).slice(1))
const mediaContentId = computed(() => Number(data.value?.header?.image?.destinationContentId))
const product = computed(() => data.value?.header && getProductFromResource(data.value?.header))
const productLink = computed(() => product.value ? resolveBaseUrl(product.value) : '')
const showTeacherButton = computed(() => isTeacher.value && data.value?.header)

const headerStyle = computed(() => {
  const id = data.value?.header?.stencil?.identifier
  return (id && id !== 'none') && { background: forceColorShade(theme.value[60], '30') }
})

const { data: bgMedia, isLoading: bgMediaIsLoading } = firstOf(useMedia(mediaContentId.value ? [mediaContentId.value] : []))

const getViewMode = (header: ContentProductHeader | ContentProductPackage) => isContentHeader(header) ? header.viewMode : 'default'

onMounted(() => setAppColor({
  themeColor: theme.value[60],
  bgColor: theme.value[10],
}))

onBeforeUnmount(() => clearAppColor())

watch(data, () => {
  if (!data.value?.header)
    return
  setTitle(t('termExam.byline', { title: data.value.header.title.toLowerCase() }))
}, { immediate: true })
</script>

<template>
  <div
    class="relative grid gap-20 text-(--theme-60)"
    :style="themeStyle"
  >
    <NotFound
      v-if="isError"
      :error
      :return-path="productLink"
      class="mt-24"
    />
    <HeaderViewSkeleton v-else-if="isLoading" />
    <template v-else>
      <HeaderViewBannerGrid class="bg-(--theme-60) text-(--theme-10)">
        <Teleport to="#header-navigation">
          <Button
            v-if="product && !!productLink"
            :to="{ path: productLink }"
            :color="theme[50]"
            size="xl"
            variant="outline"
            shade="light"
          >
            <font-awesome-icon :icon="faArrowLeft" />
            {{ product.aunivers.name }}
          </Button>
        </Teleport>
        <div class="grid-in-header-info flex flex-col items-start gap-4 self-center py-24 md:py-32">
          <span
            class="tracking-wider uppercase"
            v-text="t('termExam.byline', { title: data?.header?.parentLocationName || '' })"
          />
          <h1
            class="text-4xl font-bold"
            v-text="data?.header?.title"
          />
          <RichTextRenderer
            v-if="data?.header?.intro"
            class="prose text-xl text-current"
            :text="data.header.intro"
          />
          <HeaderTeacherButton
            v-if="showTeacherButton && data?.header"
            :selected-header="data?.header"
            :data-pendo="PendoFeature.TeacherContent.Header"
          />
        </div>
        <div
          class="grid-in-image md:grid-in-image-top-full relative size-full"
          :style="headerStyle"
        >
          <HeaderStencil
            v-if="data?.header?.stencil?.identifier"
            :stencil="data?.header.stencil.identifier"
            class="absolute left-0 z-10"
          />
          <Image
            v-if="mediaContentId"
            :content="mediaContentId"
            class="aspect-video !h-auto min-h-full w-full max-w-full place-self-end object-cover object-left md:h-0 "
            :class="!bgMediaIsLoading && 'animate-fade'"
          />
        </div>
        <CopyrightButton
          v-if="bgMedia && 'copyright' in bgMedia"
          :copyright="bgMedia.copyright"
          class="col-end-[image] row-start-2 m-4 place-self-end"
        />
      </HeaderViewBannerGrid>
      <section
        v-for="(section, index) in data?.sections || []"
        :key="section.contentId"
        class="grid grid-cols-1 place-items-center last:pb-20"
        :class="{ 'bg-(--theme-20) py-20 [.py-20+&]:-mt-20 [.py-20+&]:pt-0': getViewMode(section) === 'task' }"
        :style="cardStyle(section)"
      >
        <div class="max-w-screen-au flex w-full flex-col gap-24 px-4 sm:px-8">
          <Header8To10Section
            v-if="data?.header"
            v-model:selection-map="selectionMap"
            :header="data.header"
            :section
            :index
            choose-task
            :grade-filter="false"
          />
        </div>
      </section>
      <section
        v-if="!data?.sections?.length"
        class="grid grid-cols-1 place-items-center last:pb-20"
      >
        <AuCallout
          variant="info"
          class="max-w-screen-au flex w-full flex-col gap-24 px-4 sm:px-8"
          :title="t('termExam.missing')"
        />
      </section>
    </template>
    <div v-if="data?.header && taskLocationIds.length" class="sticky bottom-0 z-10 w-full bg-(--theme-50) p-6">
      <div class="max-w-screen-au mx-auto flex px-4 sm:px-8">
        <AssignTaskDialog
          :resource="data.header"
          :task-location-ids
        />
      </div>
    </div>
  </div>
</template>
